import axios, { AxiosError }                                         from 'axios';
import { FormikHelpers }                                             from 'formik';
import qs                                                            from 'qs';
import React, { FC, useEffect, useState }                            from 'react';
import { useNavigate }                                                from 'react-router-dom';
import { LogoBlue }                                                  from '../../shared/components/Logo';
import { Notistack }                                                 from '../../shared/components/Notistack/Notistack';
import { EAuthRoutes }                                               from '../../shared/constants/constants';
import { queryParams }                                               from '../../shared/functions';
import { AuthResponseKeys }                                          from '../../shared/interfaces/auth';
import { getAuthToken, isTokenExpired }                              from '../../utils/authHelpers';
import { IResetPasswordForm, ResetPasswordForm }                     from '../ResetPassword/ResetPasswordForm';
import { IErrorResponse }                                            from '../shared/interfaces';
import { PageHeader, PageHeaderText, SignInUpPage, SignUpContainer } from '../shared/styles';

export const AcceptInvitation: FC = () => {
  const { email, code, familyId } = queryParams();
  const history = useNavigate();
  const [ isLoggedIn ] = useState(() => {
    const authToken = getAuthToken();
    return !!authToken && !isTokenExpired();
  });

  // Already-authenticated users don't need to set a password again — send them
  // straight to family selection instead of showing the accept-invitation form
  useEffect(() => {
    if (isLoggedIn) history(EAuthRoutes.SelectFamilyComponent);
  }, [ isLoggedIn ]);

  const handleSubmit =  async ({ password }: IResetPasswordForm, formikActions: FormikHelpers<IResetPasswordForm>) => {
    const requestBody = {
      email      : email.replace(/ /g, '+'),
      code,
      familyId,
      password,
      joinFamily : true,
    };

    formikActions.setSubmitting(true);

    await axios.post('/api/v1/invites/acception', requestBody)
      .then(() => {
        Notistack.enqueueSnackbar('Your password was successfully set.', 'success');
        history({
          pathname : EAuthRoutes.SignIn,
          search   : qs.stringify({ email: email.replace(/ /g, '+') }),
        });
      })
      .catch((err: AxiosError<IErrorResponse>) => {
        const errorDetails = err?.response?.data?.ErrorDetails;
        if (errorDetails) {
          const { Key, Message } = errorDetails;

          // This invite code/link was already used to set a password — the account
          // exists but the user isn't currently authenticated, so send them to sign in
          if (Key === AuthResponseKeys.INVALIDTOKEN) {
            Notistack.enqueueSnackbar('This invitation has already been accepted. Please sign in.', 'error');
            history({
              pathname : EAuthRoutes.SignIn,
              search   : qs.stringify({ email: email.replace(/ /g, '+') }),
            });
            return;
          }

          formikActions.setErrors({
            'password': Message,
          });
          formikActions.setSubmitting(false);
        }
      });
  };

  if (isLoggedIn) return null;

  return (
    <SignInUpPage>
      <SignUpContainer maxWidth="md">
        <PageHeader mb="35px">
          <LogoBlue margin="0 0 30px" />

          <PageHeaderText
            centered
            maxWidth="630px"
            padding="0 30px"
          >
            Choose Your Password to Complete Registration
          </PageHeaderText>
        </PageHeader>

        <ResetPasswordForm handleSubmit={handleSubmit} />
      </SignUpContainer>
    </SignInUpPage>
  );
};
